import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";

/**
 * OAuth / email-verification callback landing page.
 * supabase-js parses the URL fragment and stores the session automatically.
 * We just wait for a session, then redirect the user.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    let unsub;
    const finish = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        const next = params.get("next") || "/account";
        navigate(next, { replace: true });
        return true;
      }
      return false;
    };

    (async () => {
      if (await finish()) return;
      const { data } = supabase.auth.onAuthStateChange(async (event) => {
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          await finish();
        } else if (event === "PASSWORD_RECOVERY") {
          navigate("/reset-password", { replace: true });
        }
      });
      unsub = data.subscription;
      // Fallback timeout
      setTimeout(async () => {
        const { data: s } = await supabase.auth.getSession();
        if (!s.session) navigate("/login", { replace: true });
      }, 5000);
    })();

    return () => unsub?.unsubscribe?.();
  }, [navigate, params]);

  return (
    <div className="min-h-screen bg-cool-white grid place-items-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-cyan-brand border-t-transparent animate-spin" />
        <div className="text-sm text-cool-grey font-mono-data">Completing sign-in…</div>
      </div>
    </div>
  );
}
